import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import {
  checkout,
  createPortalSession,
  getEntitlement,
  getProfile,
  isInvalidEmailError,
  isPortalError,
  isTwitchSignInRequiredError,
  updateProfile,
} from '../api/rttsClient';
import type { Entitlement, Profile } from '../api/types';
import {
  STUDIO_CHECKOUT_ROUTES,
  STUDIO_COPY,
  STUDIO_DOCUMENT_TITLES,
  STUDIO_PORTAL_RETURN_ROUTE,
  STUDIO_PRICING,
  formatAllotmentCopy,
  formatCreditBalanceCopy,
  formatFreeAllotmentCopy,
  isStudioBillingEnabled,
  isStudioReelPacksEnabled,
  isStudioUseMock,
} from '../config';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import { useUser } from '../../contexts/UserContext';
import { setMockAuthState } from '../api/mockState';
import { formatResetDate } from '../utils/format';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type EditField = 'display_name' | 'email' | null;

function initialsFromProfile(profile: Profile): string {
  const source = profile.display_name || profile.twitch_handle || '?';
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase() || '?';
}

/**
 * Account — identity, plan, manage subscription (paid), sign out.
 */
const StudioAccountPage: React.FC = () => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.account);
  const navigate = useNavigate();
  const { status, isLoggedOut, refreshMock } = useStudioAuth();
  const { logout } = useUser();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<EditField>(null);
  const [draftName, setDraftName] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [portalHidden, setPortalHidden] = useState(false);
  const [packBusy, setPackBusy] = useState(false);
  const [packError, setPackError] = useState<string | null>(null);
  const draftRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [nextProfile, nextEntitlement] = await Promise.all([
        getProfile(),
        getEntitlement().catch(() => null),
      ]);
      setProfile(nextProfile);
      setEntitlement(nextEntitlement);
    } catch {
      setLoadError('Could not load your account. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoggedOut || status === 'loading') return;
    void load();
  }, [isLoggedOut, status, load]);

  useEffect(() => {
    if (editing && draftRef.current) draftRef.current.focus();
  }, [editing]);

  if (isLoggedOut) {
    return <Navigate to="/studio/connect" replace />;
  }

  if (status === 'loading' || loading) {
    return (
      <div className="flex justify-center py-16" data-testid="studio-account-loading">
        <div className="studio-spinner" />
      </div>
    );
  }

  const startEdit = (field: EditField) => {
    if (!profile || !field) return;
    setFieldError(null);
    setEditing(field);
    setDraftName(profile.display_name ?? '');
    setDraftEmail(profile.email ?? '');
  };

  const cancelEdit = () => {
    setEditing(null);
    setFieldError(null);
  };

  const saveEdit = async () => {
    if (!profile || !editing || saving) return;
    setFieldError(null);

    if (editing === 'email') {
      const trimmed = draftEmail.trim();
      if (trimmed && !EMAIL_RE.test(trimmed)) {
        setFieldError(STUDIO_COPY.invalidEmail);
        return;
      }
      setSaving(true);
      try {
        const next = await updateProfile({ email: trimmed || null });
        setProfile(next);
        setEditing(null);
      } catch (error) {
        if (isInvalidEmailError(error)) {
          setFieldError(STUDIO_COPY.invalidEmail);
        } else {
          setFieldError('Could not save. Try again.');
        }
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    try {
      const next = await updateProfile({
        display_name: draftName.trim() || null,
      });
      setProfile(next);
      setEditing(null);
    } catch {
      setFieldError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePortal = async () => {
    if (portalBusy) return;
    setPortalError(null);
    setPortalBusy(true);
    try {
      const { portal_url } = await createPortalSession();
      if (isStudioUseMock()) {
        navigate(STUDIO_PORTAL_RETURN_ROUTE);
        setPortalBusy(false);
        return;
      }
      window.location.assign(portal_url);
    } catch (error) {
      if (isPortalError(error) && error.error === 'no_active_subscription') {
        setPortalHidden(true);
        setPortalBusy(false);
        return;
      }
      setPortalError(STUDIO_COPY.portalUnavailable);
      setPortalBusy(false);
    }
  };

  const handlePackCheckout = async () => {
    if (
      packBusy ||
      !isStudioBillingEnabled() ||
      !isStudioReelPacksEnabled()
    ) {
      return;
    }
    setPackError(null);
    setPackBusy(true);
    try {
      const { checkout_url } = await checkout('pack5');
      if (isStudioUseMock()) {
        navigate(`${STUDIO_CHECKOUT_ROUTES.success}?product=pack5`);
        return;
      }
      window.location.assign(checkout_url);
    } catch (error) {
      if (isTwitchSignInRequiredError(error)) {
        if (isStudioUseMock()) {
          setMockAuthState('stale_link');
          refreshMock();
        }
        setPackBusy(false);
        return;
      }
      setPackError(STUDIO_COPY.checkoutUnavailable);
      setPackBusy(false);
    }
  };

  const handleSignOut = async () => {
    if (isStudioUseMock()) {
      setMockAuthState('logged_out');
      refreshMock();
    }
    await logout();
    navigate('/studio', { replace: true });
  };

  const isPaid = entitlement?.tier === 'paid';
  const isFree = !entitlement || entitlement.tier === 'free';
  const isAdmin =
    entitlement?.tier === 'admin' ||
    entitlement?.tier === 'comped' ||
    entitlement?.unlimited === true;
  const canManageSubscription =
    !portalHidden &&
    isPaid &&
    !isAdmin &&
    entitlement?.can_manage_subscription !== false;

  return (
    <div className="studio-page" data-testid="studio-account">
      <div className="studio-section">
        <h1 className="studio-display text-3xl">{STUDIO_COPY.accountHeading}</h1>
        <p className="studio-page-kicker">{STUDIO_COPY.accountSubhead}</p>
      </div>

      {loadError || !profile ? (
        <div
          className="studio-panel space-y-3"
          role="alert"
        >
          <p>{loadError ?? STUDIO_COPY.loadAccountFailed}</p>
          <button type="button" className="studio-btn-ghost" onClick={() => void load()}>
            {STUDIO_COPY.tryAgain}
          </button>
        </div>
      ) : (
        <>
          <section
            className="studio-panel space-y-4"
            data-testid="studio-account-identity"
          >
            <div className="flex items-center gap-4">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt=""
                  className="studio-avatar-img"
                  data-testid="studio-account-avatar"
                />
              ) : (
                <div
                  className="studio-avatar-fallback"
                  aria-hidden
                  data-testid="studio-account-avatar-fallback"
                >
                  {initialsFromProfile(profile)}
                </div>
              )}
              <div className="min-w-0">
                <p className="font-medium truncate" data-testid="studio-account-handle">
                  {profile.twitch_handle ? `@${profile.twitch_handle}` : 'Twitch connected'}
                </p>
                <p className="text-sm text-[var(--studio-muted)]">Twitch identity</p>
              </div>
            </div>

            <div className="space-y-3 border-t border-white/5 pt-4">
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-[var(--studio-muted)]">Display name</span>
                  {editing !== 'display_name' ? (
                    <button
                      type="button"
                      className="text-sm text-[var(--studio-accent-bright)]"
                      onClick={() => startEdit('display_name')}
                      data-testid="studio-account-edit-name"
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
                {editing === 'display_name' ? (
                  <div className="flex flex-wrap gap-2">
                    <input
                      ref={draftRef}
                      className="studio-input flex-1 min-w-[12rem]"
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      data-testid="studio-account-name-input"
                    />
                    <button
                      type="button"
                      className="studio-btn-primary px-3 py-2 text-sm"
                      onClick={() => void saveEdit()}
                      disabled={saving}
                      data-testid="studio-account-name-save"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="studio-btn-ghost px-3 py-2 text-sm"
                      onClick={cancelEdit}
                      data-testid="studio-account-name-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <p data-testid="studio-account-display-name">
                    {profile.display_name || '—'}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-[var(--studio-muted)]">Email</span>
                  {editing !== 'email' ? (
                    <button
                      type="button"
                      className="text-sm text-[var(--studio-accent-bright)]"
                      onClick={() => startEdit('email')}
                      data-testid="studio-account-edit-email"
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
                {editing === 'email' ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <input
                        ref={draftRef}
                        type="email"
                        className="studio-input flex-1 min-w-[12rem]"
                        value={draftEmail}
                        onChange={(e) => {
                          setDraftEmail(e.target.value);
                          setFieldError(null);
                        }}
                        data-testid="studio-account-email-input"
                        aria-invalid={Boolean(fieldError)}
                      />
                      <button
                        type="button"
                        className="studio-btn-primary px-3 py-2 text-sm"
                        onClick={() => void saveEdit()}
                        disabled={saving}
                        data-testid="studio-account-email-save"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="studio-btn-ghost px-3 py-2 text-sm"
                        onClick={cancelEdit}
                        data-testid="studio-account-email-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p data-testid="studio-account-email">{profile.email || '—'}</p>
                )}
                {fieldError ? (
                  <p
                    className="text-sm text-[var(--studio-danger)]"
                    role="alert"
                    data-testid="studio-account-field-error"
                  >
                    {fieldError}
                  </p>
                ) : null}
              </div>
            </div>
          </section>

          <section
            className="studio-panel space-y-3"
            data-testid="studio-account-plan"
          >
            <h2 className="studio-page-heading">{STUDIO_COPY.accountPlanHeading}</h2>
            {isAdmin ? (
              <p data-testid="studio-account-plan-admin">{STUDIO_COPY.accountAdminUnlimited}</p>
            ) : null}
            {isPaid && entitlement ? (
              <div className="space-y-2" data-testid="studio-account-plan-paid">
                <p className="font-medium">{STUDIO_COPY.paidPlanLabel}</p>
                <p className="text-sm text-[var(--studio-muted)]" data-testid="studio-account-plan-allotment">
                  {formatAllotmentCopy(
                    entitlement.period_reels_remaining ?? 0,
                    entitlement.period_cap ?? STUDIO_PRICING.paidCap,
                    entitlement.period_resets_at
                      ? formatResetDate(entitlement.period_resets_at)
                      : undefined
                  )}
                </p>
                {canManageSubscription ? (
                  <button
                    type="button"
                    className="studio-btn-ghost"
                    onClick={() => void handlePortal()}
                    disabled={portalBusy}
                    aria-busy={portalBusy}
                    data-testid="studio-account-manage-subscription"
                  >
                    {portalBusy
                      ? STUDIO_COPY.portalOpening
                      : STUDIO_COPY.accountManageSubscription}
                  </button>
                ) : null}
                {canManageSubscription && portalError ? (
                  <p
                    className="text-sm text-[var(--studio-danger)]"
                    role="alert"
                    data-testid="studio-account-portal-error"
                  >
                    {portalError}
                  </p>
                ) : null}
              </div>
            ) : null}
            {isFree && !isAdmin ? (
              <div className="space-y-2" data-testid="studio-account-plan-free">
                <p className="font-medium">{STUDIO_COPY.freePlanLabel}</p>
                <p
                  className="text-sm text-[var(--studio-muted)]"
                  data-testid="studio-account-plan-allotment-free"
                >
                  {entitlement
                    ? formatFreeAllotmentCopy(
                        entitlement.reels_remaining ?? 0,
                        entitlement.free_cap ?? STUDIO_PRICING.free.reels
                      )
                    : null}
                </p>
                {(entitlement?.reel_credits ?? 0) > 0 ? (
                  <p
                    className="text-sm text-[var(--studio-text)]"
                    data-testid="studio-account-credit-balance"
                  >
                    {formatCreditBalanceCopy(entitlement?.reel_credits ?? 0)}
                  </p>
                ) : null}
                {isStudioBillingEnabled() ? (
                  <div className="flex flex-col items-start gap-2">
                    {isStudioReelPacksEnabled() ? (
                      <button
                        type="button"
                        className="studio-btn-ghost"
                        onClick={() => void handlePackCheckout()}
                        disabled={packBusy}
                        data-testid="studio-account-pack-cta"
                      >
                        {packBusy
                          ? STUDIO_COPY.checkoutPending
                          : STUDIO_COPY.accountPackCta}
                      </button>
                    ) : null}
                    <Link
                      to="/studio/upgrade"
                      className="studio-btn-primary no-underline inline-flex"
                      data-testid="studio-account-upgrade"
                    >
                      {STUDIO_COPY.accountUpgradeLink}
                    </Link>
                    {packError ? (
                      <p className="text-sm text-[var(--studio-danger)]" role="alert">
                        {packError}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <Link
                    to="/studio/upgrade"
                    className="studio-btn-ghost no-underline inline-flex"
                    data-testid="studio-account-see-plans"
                  >
                    {STUDIO_COPY.seePlansCta}
                  </Link>
                )}
              </div>
            ) : null}
          </section>

          <section className="studio-panel space-y-3">
            <h2 className="text-lg font-semibold">{STUDIO_COPY.reportProblem}</h2>
            <Link
              to="/studio/support"
              className="studio-btn-ghost no-underline inline-flex"
              data-testid="studio-account-support"
            >
              {STUDIO_COPY.reportProblem}
            </Link>
          </section>

          <button
            type="button"
            className="studio-btn-ghost w-full"
            onClick={() => void handleSignOut()}
            data-testid="studio-account-sign-out"
          >
            {STUDIO_COPY.accountSignOut}
          </button>
        </>
      )}
    </div>
  );
};

export default StudioAccountPage;
